import { Injectable } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { Observable, of } from "rxjs";
import { tap, map, switchMap, catchError } from "rxjs/operators";
import { PurchaseOrder, PurchaseOrderCancelReason } from "../interfaces/purchase-order.interface";
import { PurchaseOrderUtils } from "../utilities/purchase-order";

@Injectable()
export class PurchaseOrdersService {
  constructor(private http: HttpClientService) {}

  /** Reasons offered when cancelling an order, owned by the backend so both sides share one catalog. */
  public getCancellationReasons(): Observable<PurchaseOrderCancelReason[]> {
    return this.http.get("/api/purchase_order_cancellation_reasons").pipe(
      map((response: { cancellation_reasons?: PurchaseOrderCancelReason[] }) => response?.cancellation_reasons || []),
      tap((_) => console.log("PurchaseOrdersService.getCancellationReasons: Ok."))
    );
  }

  public getPurchaseOrder(id): Observable<any> {
    return this.http.get(`/api/purchase_orders/${id}`).pipe(
      map((response: any) => PurchaseOrderUtils.transformPurchaseOrderResponse(response)),
      tap((_) => console.log("PurchaseOrdersService.getPurchaseOrder: Ok."))
    );
  }

  public updatePurchaseData(id: number, purchase_order: PurchaseOrder): Observable<any> {
    const options = {
      purchase_order,
    };
    return this.http
      .put(`/api/purchase_orders/${id}/data_update`, options)
      .pipe(tap((_) => console.log("PurchaseOrdersService.updatePurchaseData: Ok.")));
  }

  public updatePurchase(purchase_order: any): Observable<any> {
    const options = {
      purchase_order,
    };
    return this.http
      .put(`/api/purchase_orders/${purchase_order.id}`, options)
      .pipe(tap((_) => console.log("PurchaseOrdersService.updatePurchase: Ok.")));
  }

  public requestPurchaseOrder(purchase_order_id: number): Observable<any> {
    return this.http
      .post(`/api/purchase_orders/${purchase_order_id}/request_purchase`, {})
      .pipe(tap((_) => console.log("PurchaseOrdersService.requestPurchaseOrder: Ok.")));
  }

  public requestPurchaseOrderReturnToRequestedQueue(purchase_order_id: number): Observable<any> {
    return this.requestPurchaseOrder(purchase_order_id).pipe(
      switchMap((po: any) => {
        if (po?.status === "missing_documents") {
          return this.updatePurchase({ id: po.id, status: "requested" }).pipe(catchError(() => of(po)));
        }
        return of(po);
      })
    );
  }

  public getAllPurchaseOrders({
    status = undefined,
    person_id = undefined,
    page = undefined,
    page_size = undefined,
    prevalidation = undefined,
    processing = undefined,
    under_nexcar_review = undefined,
    view_as = undefined,
    include_all_prevalidations = undefined,
  }) {
    const options = {
      params: {
        status,
        person_id,
        page,
        page_size,
        prevalidation,
        processing,
        under_nexcar_review,
        "view_as[]": view_as,
        include_all_prevalidations,
      },
    };
    return this.http
      .get("/api/purchase_orders", options)
      .pipe(tap((_) => console.log("PurchaseOrdersService.getAllPurchaseOrders: Ok.")));
  }

  public searchPurchaseOrders({
    start_date = undefined,
    end_date = undefined,
    person_ids = undefined,
    name = undefined,
    application_id = undefined,
  }) {
    const options = {
      params: {
        start_date,
        end_date,
        person_ids,
        name,
        application_id,
      },
    };
    return this.http
      .get("/api/purchase_orders/search", options)
      .pipe(tap((_) => console.log("PurchaseOrdersService.searchPurchaseOrders: Ok.")));
  }

  public createRevisionTimekeeper(id, from, to) {
    const params = {
      from,
      to,
    };
    return this.http
      .post(`/api/purchase_orders/${id}/create_revision_time`, params)
      .pipe(tap((_) => console.log("PurchaseOrdersService.createRevisionTimekeeper: Ok.")));
  }

  public reloadData(id): Observable<any> {
    return this.http
      .put(`/api/purchase_orders/${id}/reload_data`, {})
      .pipe(tap((_) => console.log("PurchaseOrdersService.reloadData: Ok.")));
  }

  public getDocsByStep(id, { status = "undefined" }) {
    const options = {
      params: {
        status,
      },
    };
    return this.http
      .get(`/api/purchase_orders/${id}/docs_by_step`, options)
      .pipe(tap((_) => console.log("PurchaseOrdersService.getDocsByStep: Ok.")));
  }

  public recoverFilesFromLastReview(id) {
    return this.http
      .post(`/api/purchase_orders/${id}/move_docs_oc`, {})
      .pipe(tap((_) => console.log("PurchaseOrdersService.recoverFilesFromLastReview: Ok.")));
  }

  public verifyRecoveryExpedient(id) {
    return this.http
      .get(`/api/purchase_orders/${id}/verify_recovery_expedient`, {})
      .pipe(tap((_) => console.log("PurchaseOrdersService.verifyRecoveryExpedient: Ok.")));
  }

  public getSentPurchaseOrders(page_size = 10, page = 1) {
    const options = {
      params: {
        page_size: page_size.toString(),
        page: page.toString(),
      },
    };
    return this.http
      .get("/api/purchase_orders/sent", options)
      .pipe(tap((_) => console.log("PurchaseOrdersService.getSentPurchaseOrders: Ok.")));
  }

  public getDetails(id) {
    return this.http
      .get(`/api/purchase_orders/${id}/details`)
      .pipe(tap((_) => console.log("PurchaseOrdersService.getDetails: Ok.")));
  }

  public getLetter(id): Observable<any> {
    const options = {
      responseType: "blob" as "json",
    };
    return this.http
      .get(`/api/purchase_orders/${id}/letter`, options)
      .pipe(tap((_) => console.log("PurchaseOrdersService.getLetter: Ok.")));
  }

  public getByLead(lead) {
    return this.http
      .get(`/api/persons/${lead}/sent_purchase_orders`)
      .pipe(tap((_) => console.log("PurchaseOrdersService.getByLead: Ok.")));
  }

  public getRepuveInfoAI(id, document_id) {
    return this.http
      .get(`/api/purchase_orders/${id}/repuve_info_ai/${document_id}`, {})
      .pipe(tap((_) => console.log("PurchaseOrdersService.getRepuveInfoAI: Ok.")));
  }

  public updatePurchaseDataAgencyInfo(id: number, agency_info): Observable<any> {
    const options = {
      agency_info,
    };
    return this.http
      .put(`/api/purchase_orders/${id}/agency_info`, options)
      .pipe(tap((_) => console.log("PurchaseOrdersService.updatePurchaseDataAgencyInfo: Ok.")));
  }

  public updatePurchaseDataCarInfo(id: number, car_info): Observable<any> {
    const options = {
      car_info,
    };
    return this.http
      .put(`/api/purchase_orders/${id}/car_info`, options)
      .pipe(tap((_) => console.log("PurchaseOrdersService.updatePurchaseDataCarInfo: Ok.")));
  }

  public updatePurchaseDataClientInfo(id: number, client_info): Observable<any> {
    const options = {
      client_info,
    };
    return this.http
      .put(`/api/purchase_orders/${id}/client_info`, options)
      .pipe(tap((_) => console.log("PurchaseOrdersService.updatePurchaseDataClientInfo: Ok.")));
  }

  public resolveIncident(id: number, comment: string): Observable<any> {
    return this.http
      .put(`/api/purchase_orders/${id}/resolve_incident`, { comment })
      .pipe(tap((_) => console.log("PurchaseOrdersService.resolveIncident: Ok.")));
  }

  public removeIncidentsFromDocByCategory(id, category) {
    const options = {
      category,
    };
    return this.http
      .put(`/api/purchase_orders/${id}/remove_doc_incidents_by_category`, options)
      .pipe(tap((_) => console.log("PurchaseOrdersService.removeIncidentsFromDocByCategory: Ok.")));
  }
}
